import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import TranslatedContent from '../components/TranslatedContent';
import { AutoTranslate } from '../hooks/useAutoTranslate';
import { useLanguage } from '../context/LanguageContext';
import { getArticles } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { BookOpen, Calendar, Heart, Users, ArrowRight, X, ExternalLink, GraduationCap, Cross, Church, UserPlus, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import usePageBanner from '../hooks/usePageBanner';
import { createSanitizedMarkup } from '../utils/sanitize';
import SocialShareButtons from '../components/SocialShareButtons';

const FordjupningPage = () => {
  const { t, language, isRTL } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const { bannerImage, bannerTitle, bannerSubtitle } = usePageBanner('fordjupning', language);

  // Static content for search across all Fördjupning pages - use Swedish text for AutoTranslate
  const staticContent = [
    { 
      title: 'Den Kristna Läran', 
      description: 'Böner, bud och sanningar för den kristna tron. Korstecknet, Fader vår, Ave Maria, Tio Guds bud.',
      path: '/fordjupning/kristna-laran',
      type: 'page'
    },
    { 
      title: 'Stiftelser', 
      description: 'Kyrkans sju sakrament, bikt, dop, vigsel, begravning, medlemskap.',
      path: '/fordjupning/stiftelser',
      type: 'page'
    },
    { 
      title: 'Mässor & Böner', 
      description: 'Botdagar, böner, söndagsblad, mässordning på olika språk.',
      path: '/fordjupning/masses',
      type: 'page'
    },
    { 
      title: 'Bli katolik', 
      description: 'Konversion, vuxendop, katekumenatet, vägen till katolska kyrkan.',
      path: '/fordjupning/convert',
      type: 'page'
    },
  ];

  useEffect(() => {
    loadArticles();
  }, []);

  // Deep-link: ?article=<id> opens the article modal on mount / after articles load
  useEffect(() => {
    if (!articles.length || typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash || '';
    const hashId = hash.startsWith('#article-') ? hash.slice('#article-'.length) : null;
    const targetId = params.get('article') || hashId;
    if (!targetId) return;
    const target = articles.find((a) => String(a.id) === String(targetId));
    if (target) {
      setSelectedArticle(target);
    }
  }, [articles]);

  const loadArticles = async () => {
    try {
      const data = await getArticles();
      setArticles(data || []);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredArticles = articles;

  // Search across articles and static pages
  const searchResults = searchQuery.trim() ? [
    ...staticContent.filter(page => 
      page.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      page.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
    ...articles.filter(article => {
      const title = article.title?.sv || article.title || '';
      const content = article.content?.sv || article.content || '';
      return title.toLowerCase().includes(searchQuery.toLowerCase()) ||
             content.toLowerCase().includes(searchQuery.toLowerCase());
    }).map(article => ({
      ...article,
      type: 'article'
    }))
  ] : [];

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'faith': return <Heart className="w-4 h-4" />;
      case 'social': return <Users className="w-4 h-4" />;
      case 'education': return <GraduationCap className="w-4 h-4" />;
      default: return <BookOpen className="w-4 h-4" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'faith': return 'bg-purple-100 text-purple-800';
      case 'social': return 'bg-blue-100 text-blue-800';
      case 'education': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'faith': return t('faithArticles');
      case 'social': return t('socialArticles');
      case 'education': return t('educationArticles');
      default: return category;
    }
  };

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden">
        <img
          src={bannerImage || 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=1920'}
          alt="Fördjupning"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/40 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              {bannerTitle ? (
                <TranslatedContent content={{ sv: bannerTitle }} as="span" />
              ) : (
                <AutoTranslate>{t('fordjupningTitle') || 'Fördjupning'}</AutoTranslate>
              )}
            </h1>
            <p className="text-xl text-gray-200 fade-in">
              {bannerSubtitle ? (
                <TranslatedContent content={{ sv: bannerSubtitle }} as="span" />
              ) : (
                <AutoTranslate>{t('fordjupningSubtitle') || 'Artiklar och reflektioner om tro och samhälle'}</AutoTranslate>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-6 bg-gray-50 border-b">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder={t('searchPlaceholder') || 'Sök...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-3 w-full text-lg"
              data-testid="fordjupning-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Search Results */}
          {searchQuery && searchResults.length > 0 && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-gray-500">
                {searchResults.length} {t('results') || 'resultat'}
              </p>
              {searchResults.map((result, idx) => (
                <Link 
                  key={result.id || `${result.path}-${idx}`}
                  to={result.path || '#'}
                  onClick={() => {
                    if (result.type === 'article') {
                      setSelectedArticle(result);
                      setSearchQuery('');
                    }
                  }}
                  className="block"
                >
                  <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                        {result.type === 'page' ? (
                          <BookOpen className="w-3 h-3" />
                        ) : (
                          getCategoryIcon(result.category)
                        )}
                        <span>{result.type === 'page' ? <AutoTranslate>Sida</AutoTranslate> : getCategoryLabel(result.category)}</span>
                      </div>
                      <h4 className="font-semibold text-gray-900">
                        <AutoTranslate>{result.title?.sv || result.title}</AutoTranslate>
                      </h4>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                        <AutoTranslate>{result.description || (result.content?.sv?.substring(0, 100) + '...')}</AutoTranslate>
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
          
          {searchQuery && searchResults.length === 0 && (
            <p className="mt-4 text-center text-gray-500">
              <AutoTranslate>Inga resultat hittades</AutoTranslate>
            </p>
          )}
        </div>
      </section>

      {/* Kristna Läran Feature Card */}
      <section className="py-8 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/fordjupning/kristna-laran" className="block group" data-testid="kristna-laran-link">
            <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row items-center">
                  <div className="w-full md:w-1/3 h-48 md:h-auto bg-gradient-to-br from-green-600 to-emerald-700 flex items-center justify-center p-8">
                    <div className="text-center text-white">
                      <Cross className="w-16 h-16 mx-auto mb-3 group-hover:scale-110 transition-transform" />
                      <Church className="w-10 h-10 mx-auto opacity-70" />
                    </div>
                  </div>
                  <div className="flex-1 p-8">
                    <div className="flex items-center gap-2 text-green-600 text-sm font-medium mb-2">
                      <BookOpen className="w-4 h-4" />
                      <AutoTranslate>Undervisning</AutoTranslate>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-serif font-bold text-gray-900 mb-3 group-hover:text-green-700 transition-colors">
                      <AutoTranslate>Den Kristna Läran</AutoTranslate>
                    </h3>
                    <p className="text-gray-600 mb-4">
                      <AutoTranslate>Böner, bud och sanningar för den kristna tron. Utforska korstecknet, Herrens bön, Trosbekännelsen, Ave Maria, Tio Guds bud och mycket mer.</AutoTranslate>
                    </p>
                    <div className="flex items-center text-green-600 font-medium group-hover:text-green-700">
                      <AutoTranslate>Utforska läran</AutoTranslate>
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Convert Feature Card */}
      <section className="py-8 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/fordjupning/convert" className="block group" data-testid="convert-link">
            <Card className="border-0 shadow-xl card-hover hover:shadow-xl transition-all duration-300 overflow-hidden">
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row items-center">
                  <div className="w-full md:w-1/3 h-48 md:h-auto bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-8">
                    <div className="text-center text-white">
                      <div className="flex justify-center gap-2 mb-3">
                        <Users className="w-10 h-10 group-hover:scale-110 transition-transform" />
                      </div>
                      <Church className="w-16 h-16 mx-auto group-hover:scale-110 transition-transform" />
                    </div>
                  </div>
                  <div className="flex-1 p-8">
                    <div className="flex items-center gap-2 text-blue-600 text-sm font-medium mb-2">
                      <UserPlus className="w-4 h-4" />
                      <AutoTranslate>Välkommen</AutoTranslate>
                    </div>
                    <h3 className="text-2xl md:text-3xl font-serif font-bold text-gray-900 mb-3 group-hover:text-blue-700 transition-colors">
                      <AutoTranslate>Vill du bli katolik?</AutoTranslate>
                    </h3>
                    <p className="text-gray-600 mb-4">
                      <AutoTranslate>Upptäck vägen till den katolska tron. Lär dig om katekumenatet, sakramenten och hur du kan bli en del av vår gemenskap.</AutoTranslate>
                    </p>
                    <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700">
                      <AutoTranslate>Läs mer</AutoTranslate>
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>

      {/* Articles Content */}
      <section className="py-16" data-testid="articles-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-gray-600">{t('loading')}</p>
            </div>
          ) : filteredArticles.length === 0 ? (
            <Card className="p-12 text-center border-0 shadow-xl card-hover">
              <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-2xl font-serif font-bold text-gray-900 mb-2">
                {t('noArticles')}
              </h3>
              <p className="text-gray-600">
                <AutoTranslate>Kom tillbaka snart för fler artiklar och reflektioner!</AutoTranslate>
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => {
                const shareUrl =
                  typeof window !== 'undefined'
                    ? `${window.location.origin}/api/share/article/${encodeURIComponent(article.id)}`
                    : '';
                return (
                <Card 
                  key={article.id} 
                  className="overflow-hidden card-hover border-0 shadow-lg h-full flex flex-col"
                  data-testid={`article-${article.id}`}
                >
                  {article.featured_image && (
                    <div className="h-48 overflow-hidden">
                      <img
                        src={article.featured_image}
                        alt={article.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <CardContent className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center space-x-2 text-xs text-gray-500 mb-3">
                      <Calendar className="w-3 h-3" />
                      <span>{new Date(article.published_at).toLocaleDateString('sv-SE')}</span>
                    </div>
                    <TranslatedContent 
                      content={{ sv: article.title, en: article.title }}
                      as="h3"
                      className="text-lg font-serif font-bold text-gray-900 mb-3 line-clamp-2"
                    />
                    <div 
                      className="text-sm text-gray-600 line-clamp-4 mb-4 flex-1"
                      dangerouslySetInnerHTML={createSanitizedMarkup(article.excerpt || article.content?.substring(0, 200) + '...')}
                    />
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => setSelectedArticle(article)}
                    >
                      {t('readMore')}
                      <ArrowRight className="ml-2 w-3 h-3" />
                    </Button>
                    {/* Social share */}
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <SocialShareButtons
                        url={shareUrl}
                        title={article.title || ''}
                        testIdPrefix={`share-article-${article.id}`}
                      />
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Article Detail Modal */}
      <Dialog open={!!selectedArticle} onOpenChange={() => setSelectedArticle(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedArticle && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedArticle.published_at).toLocaleDateString('sv-SE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <DialogTitle className="text-3xl font-serif leading-tight">
                  <TranslatedContent 
                    content={{ sv: selectedArticle.title }}
                    as="span"
                  />
                </DialogTitle>
              </DialogHeader>
              
              {selectedArticle.featured_image && (
                <img
                  src={selectedArticle.featured_image}
                  alt={selectedArticle.title}
                  className="w-full h-72 object-cover rounded-lg my-6"
                />
              )}
              
              {/* Article Content with proper typography */}
              <article className="prose prose-lg prose-gray max-w-none mt-6">
                <TranslatedContent 
                  content={{ sv: selectedArticle.content }}
                  as="div"
                  html={true}
                />
              </article>

              {selectedArticle.external_link && (
                <div className="mt-8 pt-6 border-t">
                  <a 
                    href={selectedArticle.external_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    <AutoTranslate>Läs originalartikeln</AutoTranslate>
                  </a>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default FordjupningPage;
